import {
	Alert,
	Box,
	Collapse,
	Divider,
	Grid2,
	LinearProgress,
	Snackbar,
	Typography,
} from "@mui/material";
import { addDays, subDays } from "date-fns";
import { useConfirm } from "material-ui-confirm";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { VariableSizeList as List } from "react-window";

import FTCalendarToolbar from "../../design/ft-calendar-toolbar";
import useViewport from "../../utils/use-viewport";
import useShiftDemandController from "./controllers/use-shift-demand-controller";
import useShiftDemandData from "./controllers/use-shift-demand-data";
import {
	postFTShiftDemands,
	resetPostShiftDemandStatus,
	resetShiftDemandOpen,
} from "./store/shift-demand-slice";
import { DemandCalendarHeader } from "./ui/demand-calendar-header";
import DemandVirtualizedRow from "./ui/demand-virtualized-row";
import ShiftDemandSkeleton from "./ui/shift-demand-skeleton";

const ShiftDemand: React.FC = () => {
	const [
		{ updatedShiftDemands, searchText, editedDemands, dateRange, currentDate, startingDay },
		{ handleDemandChange, setSearchText, getRangeForSelectedDate, setCurrentDate },
	] = useShiftDemandController();
	const {
		fullTimeShiftDemandStatus,
		postShiftDemandStatus,
		postShiftDemandErrorMessage,
	} = useShiftDemandData();
	const { height } = useViewport();
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [isFilterOpen, setIsFilterOpen] = useState(false);
	const confirm = useConfirm();

	const [expandedWorkcenters, setExpandedWorkcenters] = useState<Set<string>>(
		new Set()
	);

	const listRef = useRef<List | null>(null);

	useEffect(() => {
		// If there's no data, do nothing and ensure the state is empty.
		if (!updatedShiftDemands) {
			setExpandedWorkcenters(new Set<string>());
			return;
		}

		// If there's search text, filter based on it.
		if (searchText) {
			const filteredExpanded = new Set<string>();
			updatedShiftDemands.forEach((workcenter) => {
				const hasMatch = workcenter.jobs?.some((job) => {
					const jobMatch = job.jobName
						.toLowerCase()
						.includes(searchText.toLowerCase());
					const patternMatch = job.shiftPatterns?.some((pattern) =>
						pattern.shiftPatternName
							.toLowerCase()
							.includes(searchText.toLowerCase())
					);
					return jobMatch || patternMatch;
				});

				if (hasMatch) {
					filteredExpanded.add(workcenter.workcenterId.toString());
				}
			});
			setExpandedWorkcenters(filteredExpanded);
		} else {
			// Otherwise (if no search text), set the default expanded state.
			const defaultExpanded = new Set<string>();
			updatedShiftDemands.forEach((workcenter) => {
				if (workcenter.jobs?.length > 0) {
					defaultExpanded.add(workcenter.workcenterId.toString());
				}
			});
			setExpandedWorkcenters(defaultExpanded);
		}
	}, [searchText, updatedShiftDemands]);

	const toggleWorkcenter = useCallback((workcenterId: string) => {
		setExpandedWorkcenters((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(workcenterId)) {
				newSet.delete(workcenterId);
			} else {
				newSet.add(workcenterId);
			}
			return newSet;
		});
	}, []);

	const flattenedItems = useMemo(() => {
		const flatList: any[] = [];
		updatedShiftDemands?.forEach((workcenter) => {
			flatList.push({
				type: "WORKCENTER",
				id: workcenter.workcenterId,
				name: workcenter.workcenterName,
				jobCount: workcenter.jobs?.length || 0,
				searchText: searchText,
			});

			if (expandedWorkcenters.has(workcenter.workcenterId.toString())) {
				workcenter.jobs?.forEach((job) => {
					job.shiftPatterns?.forEach((pattern, patternIndex) => {
						pattern.searchText = searchText;
						flatList.push({
							type: "PATTERN_ROW",
							id: `${job.jobId}-${pattern.shiftPatternId}`,
							// Pass the job name to the row component
							jobName: job.jobName,
							showJobName: patternIndex === 0,
							isLastPattern: patternIndex === job.shiftPatterns.length - 1,
							pattern: pattern,
							searchText: searchText,
							jobId: job.jobId,
							workcenterId: workcenter.workcenterId,
						});
					});
				});
			}
		});
		return flatList;
	}, [updatedShiftDemands, expandedWorkcenters, searchText]);

	const getItemSize = (index: number) => {
		const item = flattenedItems[index];
		if (item.type === "WORKCENTER") {
			return 49; // Height for workcenter header rows
		}
		// All pattern rows now have a consistent height as there's only one row of inputs
		return 86; // A fixed height for pattern rows. Adjust as needed.
	};

	const itemData = useMemo(
		() => ({
			items: flattenedItems,
			toggleWorkcenter,
			handleDemandChange,
			editedDemands,
			expandedWorkcenters,
		}),
		[
			flattenedItems,
			toggleWorkcenter,
			handleDemandChange,
			editedDemands,
			expandedWorkcenters,
		]
	);

	useEffect(() => {
		if (listRef.current && flattenedItems.length > 0) {
			// ...tell the list to discard all cached measurements and re-calculate.
			listRef.current.resetAfterIndex(0, true);
		}
	}, [flattenedItems]);

	const schedulerContainerHeight = height - 150;

	const handleActionWithConfirmation = useCallback((action: () => void) => {
        if (editedDemands && editedDemands?.length > 0) {
            confirm({
                title: t("shift-demand_unsaved-changes-title"),
                description: t("shift-demand_unsaved-changes-description"),
                confirmationText: t("shift-demand_yes"),
                cancellationText: t("shift-demand_no"),
            })
                .then(() => {
                    action();
                })
                .catch(() => {
                    // User clicked "No", do nothing.
                });
        } else {
            // No unsaved changes, just execute the action immediately.
            action();
        }
    }, [confirm, editedDemands, t]);

	return (
		<Box width='100%'>
			<Divider />
			<FTCalendarToolbar
				isFilterOpen={isFilterOpen}
				currentTab='SHIFTS'
				setFilterOpen={() => setIsFilterOpen(!isFilterOpen)}
				handleForward={() => {
                    handleActionWithConfirmation(() => {
                        if (fullTimeShiftDemandStatus !== "pending") {
                            const firstDayOfNextWeek = addDays(
                                new Date(dateRange?.startDate ?? new Date()),
                                7
                            );
							setCurrentDate && 
                            setCurrentDate(firstDayOfNextWeek?.toISOString());
                        }
                    });
                }}
                handleBack={() => {
                    handleActionWithConfirmation(() => {
                        if (fullTimeShiftDemandStatus !== "pending") {
                            const firstDayOfPreviousWeek = subDays(
                                new Date(dateRange?.startDate ?? new Date()),
                                7
                            );
                            setCurrentDate &&
                            setCurrentDate(firstDayOfPreviousWeek?.toISOString())
                        }
                    });
                }}
                handleToday={() => {
                    // Dispatch action to fetch today's shifts
                }}
                handleCalendarClick={(date) => {
                    handleActionWithConfirmation(() => {
                        const range = getRangeForSelectedDate?.(date);
                        if (
                            range &&
                            fullTimeShiftDemandStatus !== "pending"
                        ) {
                            setCurrentDate &&
                                setCurrentDate(range?.startDate?.toISOString())
                        }
                    });
                }}
                startingDay={dateRange?.startDate ?? new Date()}
                isLoading={false}
                resetDemandClick={() =>
                    handleActionWithConfirmation(() => dispatch(resetShiftDemandOpen()))
                }
				saveDemand={() => {
					if (editedDemands && editedDemands.length > 0) {
						dispatch(postFTShiftDemands({ demands: editedDemands }));
					}
				}}
				disableSaveDemand={editedDemands && editedDemands.length === 0}
			/>
			{fullTimeShiftDemandStatus === "pending" && (
				<>
					<LinearProgress
						variant='indeterminate'
						sx={{
							width: "100%",
							position: "relative",
							height: 5,
							zIndex: 999,
						}}
					/>
					<ShiftDemandSkeleton
					isLoading={fullTimeShiftDemandStatus === "pending"}
					/>
				</>
			)}

			{fullTimeShiftDemandStatus === "fulfilled" && (
				<Box sx={{ flexGrow: 1 }}>
					<Grid2 container wrap='nowrap' sx={{ position: "relative" }}>
						<Collapse
							orientation='horizontal'
							in={isFilterOpen}
							sx={{
								position: "absolute",
								left: 0,
								top: 0,
								zIndex: 2,
								backgroundColor: "white",
								borderRight: "1px solid rgba(0, 0, 0, 0.12)",
								height: "100%",
								width: "282px",
							}}
						>
							{/* Placeholder for filters */}
						</Collapse>
						<Box sx={{ flexGrow: 1 }}>
							<Grid2
								sx={{
									pl: isFilterOpen ? "282px" : 0,
									transition: "padding-left 0.3s ease",
								}}
							>
								<Divider />

								<Box sx={{ flexShrink: 0 }}>
									<DemandCalendarHeader
										searchText={searchText ?? ""}
										setSearchText={setSearchText}
										currentDate={currentDate}
										startingDay={startingDay}
									/>
								</Box>

								<Box
									sx={{
										flexGrow: 1,
										height: schedulerContainerHeight,
									}}
								>
									{flattenedItems.length > 0 ? (
										<List
											ref={listRef}
											height={schedulerContainerHeight}
											itemCount={flattenedItems.length}
											itemSize={getItemSize}
											itemData={itemData}
											width='100%'
										>
											{DemandVirtualizedRow}
										</List>
									) : (
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												minHeight: "200px",
											}}
										>
											<Typography>{t("shift-demand_no-shift-demands-available")}</Typography>
										</Box>
									)}
								</Box>
							</Grid2>
						</Box>
					</Grid2>
				</Box>
			)}


			<Snackbar
				open={postShiftDemandStatus === "error"}
				autoHideDuration={3000}
				onClose={() => dispatch(resetPostShiftDemandStatus())}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					elevation={6}
					variant='filled'
					severity='error'
					onClose={() => dispatch(resetPostShiftDemandStatus())}
				>
					<Box display='flex' flexDirection='column' alignItems='center'>
						<Box mb={1}>
							{postShiftDemandErrorMessage ??
								t("common_error-try-again")}
						</Box>
					</Box>
				</Alert>
			</Snackbar>
		</Box>
	);
};

ShiftDemand.displayName = "ShiftDemand";
export default React.memo(ShiftDemand);
